// src/routes/AdminRoutes.jsx
import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import AdminLoginPage from '../pages/admin/Login/AdminLoginPage.jsx'
import AdminProtectedLayout from '../layouts/AdminProtectedLayout.jsx'
import SkeletonSection from '../components/common/SkeletonSection.jsx'
import JobsList from '../pages/admin/Jobs/JobsList.jsx'

// Lazy load admin pages
const AdminDashboardPage = lazy(() => import('../pages/admin/Dashboard/AdminDashboardPage.jsx'))
const CMSPageList = lazy(() => import('../pages/admin/CMS/CMSPageList.jsx'))
const CMSPageDetail = lazy(() => import('../pages/admin/CMS/CMSPageDetail.jsx'))
const ContactSubmissionsPage = lazy(() => import('../pages/admin/Submissions/ContactSubmissionsPage.jsx'))
const EnquirySubmissionsPage = lazy(() => import('../pages/admin/Submissions/EnquirySubmissionsPage.jsx'))
const NewsletterSubmissionsPage = lazy(() => import('../pages/admin/Submissions/NewsletterSubmissionsPage.jsx'))
const CareerSubmissionsPage = lazy(() => import('../pages/admin/Submissions/CareerSubmissionsPage.jsx'))
const ThemeSettingsPage = lazy(() => import('../pages/admin/Settings/ThemeSettingsPage.jsx'))
const EmailSettingsPage = lazy(() => import('../pages/admin/Settings/EmailSettingsPage.jsx'))
const HomeSettingsPage = lazy(() => import('../pages/admin/Settings/HomeSettingsPage.jsx'))
const EmailNotificationsList = lazy(() => import('../pages/admin/EmailNotifications/EmailNotificationsList.jsx'))
const ServicesList = lazy(() => import('../pages/admin/Services/ServiceList.jsx'))
const IndustriesList = lazy(() => import('../pages/admin/Industries/IndustriesList.jsx'))
const BlogsList = lazy(() => import('../pages/admin/Blogs/BlogsList.jsx'))
const CaseStudiesList = lazy(() => import('../pages/admin/CaseStudies/CaseStudiesList.jsx'))
const WebinarsList = lazy(() => import('../pages/admin/Webinars/WebinarsList.jsx'))
const TestimonialsList = lazy(() => import('../pages/admin/Testimonials/TestimonialsList.jsx'))

// Helper function to check authentication
const checkAuthStatus = () => {
    const token = localStorage.getItem('token')
    const userStr = localStorage.getItem('user')

    if (!token || !userStr) {
        return { isAuthenticated: false, user: null }
    }

    try {
        const user = JSON.parse(userStr)
        return {
            isAuthenticated: true,
            user,
            isAdmin: user?.role === 'admin'
        }
    } catch (error) {
        console.error('Error parsing user data:', error)
        // Clear invalid data
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        return { isAuthenticated: false, user: null }
    }
}

// Public route wrapper for login - prevents authenticated users from accessing login page
const PublicRoute = ({ children }) => {
    const authStatus = checkAuthStatus()

    // If user is authenticated and is admin, redirect to admin dashboard
    if (authStatus.isAuthenticated && authStatus.isAdmin) {
        return <Navigate to="/admin/cms" replace />
    }

    // If user is authenticated but not admin, clear storage and allow login
    if (authStatus.isAuthenticated && !authStatus.isAdmin) {
        localStorage.removeItem('token')
        localStorage.removeItem('user')
    }

    return children
}

// Protected route wrapper (alternative for simple cases)
const ProtectedRoute = ({ children }) => {
    const authStatus = checkAuthStatus()

    if (!authStatus.isAuthenticated || !authStatus.isAdmin) {
        return <Navigate to="/admin/login" replace />
    }

    return children
}

// Component to handle initial redirects from /admin
const AdminRootRedirect = () => {
    const authStatus = checkAuthStatus()

    if (authStatus.isAuthenticated && authStatus.isAdmin) {
        // User is authenticated admin, redirect to dashboard
        return <Navigate to="/admin/cms" replace />
    }

    // Not authenticated or not admin, go to login
    return <Navigate to="/admin/login" replace />
}

// Loading fallback component
const AdminLoadingFallback = () => (
    <div className="min-h-screen bg-gray-50 p-8">
        <SkeletonSection />
    </div>
)

// Main AdminRoutes component
export default function AdminRoutes() {
    return (
        <Routes>
            {/* Root /admin route - smart redirect based on auth state */}
            <Route index element={<AdminRootRedirect />} />

            {/* Public login route - only accessible when not authenticated as admin */}
            <Route
                path="login"
                element={
                    <PublicRoute>
                        <AdminLoginPage />
                    </PublicRoute>
                }
            />

            {/* All protected admin routes wrapped in AdminProtectedLayout */}
            <Route element={<AdminProtectedLayout />}>
                {/* Dashboard */}
                <Route
                    path="dashboard"
                    element={
                        <Suspense fallback={<AdminLoadingFallback />}>
                            <ProtectedRoute>
                                <AdminDashboardPage />
                            </ProtectedRoute>
                        </Suspense>
                    }
                />

                {/* CMS Pages */}
                <Route
                    path="cms"
                    element={
                        <Suspense fallback={<AdminLoadingFallback />}>
                            <CMSPageList />
                        </Suspense>
                    }
                />
                <Route
                    path="cms/pages/:slug"
                    element={
                        <Suspense fallback={<AdminLoadingFallback />}>
                            <CMSPageDetail />
                        </Suspense>
                    }
                />

                {/* Content Management */}
                <Route
                    path="blogs"
                    element={
                        <Suspense fallback={<AdminLoadingFallback />}>
                            <BlogsList />
                        </Suspense>
                    }
                />
                <Route
                    path="case-studies"
                    element={
                        <Suspense fallback={<AdminLoadingFallback />}>
                            <CaseStudiesList />
                        </Suspense>
                    }
                />
                <Route
                    path="webinars"
                    element={
                        <Suspense fallback={<AdminLoadingFallback />}>
                            <WebinarsList />
                        </Suspense>
                    }
                />
                <Route
                    path="testimonials"
                    element={
                        <Suspense fallback={<AdminLoadingFallback />}>
                            <TestimonialsList />
                        </Suspense>
                    }
                />
                <Route
                    path="jobs"
                    element={
                        <Suspense fallback={<AdminLoadingFallback />}>
                            <JobsList />
                        </Suspense>
                    }
                />
                <Route
                    path="services"
                    element={
                        <Suspense fallback={<AdminLoadingFallback />}>
                            <ServicesList />
                        </Suspense>
                    }
                />
                <Route
                    path="industries"
                    element={
                        <Suspense fallback={<AdminLoadingFallback />}>
                            <IndustriesList />
                        </Suspense>
                    }
                />

                {/* Submissions */}
                <Route
                    path="submissions/contact"
                    element={
                        <Suspense fallback={<AdminLoadingFallback />}>
                            <ContactSubmissionsPage />
                        </Suspense>
                    }
                />
                <Route
                    path="submissions/enquiry"
                    element={
                        <Suspense fallback={<AdminLoadingFallback />}>
                            <EnquirySubmissionsPage />
                        </Suspense>
                    }
                />
                <Route
                    path="submissions/newsletter"
                    element={
                        <Suspense fallback={<AdminLoadingFallback />}>
                            <NewsletterSubmissionsPage />
                        </Suspense>
                    }
                />
                <Route
                    path="submissions/career"
                    element={
                        <Suspense fallback={<AdminLoadingFallback />}>
                            <CareerSubmissionsPage />
                        </Suspense>
                    }
                />

                {/* Settings */}
                <Route
                    path="settings/theme"
                    element={
                        <Suspense fallback={<AdminLoadingFallback />}>
                            <ThemeSettingsPage />
                        </Suspense>
                    }
                />
                <Route
                    path="settings/home"
                    element={
                        <Suspense fallback={<AdminLoadingFallback />}>
                            <HomeSettingsPage />
                        </Suspense>
                    }
                />
                <Route
                    path="settings/email"
                    element={
                        <Suspense fallback={<AdminLoadingFallback />}>
                            <EmailNotificationsList />
                        </Suspense>
                    }
                />
                {/* <Route
                    path="settings/email"
                    element={
                        <Suspense fallback={<AdminLoadingFallback />}>
                            <EmailSettingsPage />
                        </Suspense>
                    }
                /> */}

                {/* Catch-all for protected routes - redirect to CMS */}
                <Route path="*" element={<Navigate to="/admin/cms" replace />} />
            </Route>

            {/* Catch-all route for unmatched paths under /admin */}
            <Route path="*" element={<Navigate to="/admin/login" replace />} />
        </Routes>
    )
}